import { useMemo, useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { CatalogModel } from '@/types/catalog'
import { ScoreBar } from '@/components/catalog/ScoreBar'
import { cn } from '@/lib/utils'

interface ModelListProps {
  models: CatalogModel[]
  selectedModelId: string | null
  onSelect: (modelId: string) => void
  className?: string
}

function formatPrice(model: CatalogModel) {
  if (model.pricing.isFree) return 'Free'
  return `$${model.pricing.inputPerMillion}/$${model.pricing.outputPerMillion}/M`
}

export function ModelList({ models, selectedModelId, onSelect, className }: ModelListProps) {
  const parentRef = useRef<HTMLDivElement>(null)

  const rowVirtualizer = useVirtualizer({
    count: models.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 92,
    overscan: 8,
  })

  const virtualItems = rowVirtualizer.getVirtualItems()

  const empty = models.length === 0

  return (
    <section
      className={cn('flex min-h-0 flex-1 flex-col', className)}
      data-testid="catalog-model-list"
    >
      <div className="border-b border-border px-3 py-2 text-xs text-muted-foreground">
        {models.length} models
      </div>
      <div ref={parentRef} className="min-h-0 flex-1 overflow-auto">
        {empty ? (
          <p className="p-4 text-sm text-muted-foreground">No models match your filters.</p>
        ) : (
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualItems.map((virtualRow) => {
              const model = models[virtualRow.index]
              const active = model.id === selectedModelId
              return (
                <button
                  key={model.id}
                  type="button"
                  data-testid={`catalog-model-row-${model.id}`}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  onClick={() => onSelect(model.id)}
                  className={cn(
                    'absolute left-0 top-0 w-full border-b border-border px-3 py-3 text-left transition-colors',
                    active ? 'bg-accent' : 'hover:bg-muted/60',
                  )}
                  style={{
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{model.displayName}</p>
                      <p className="line-clamp-1 text-xs text-muted-foreground">{model.description}</p>
                    </div>
                    <span className="shrink-0 text-xs font-mono text-muted-foreground">
                      {formatPrice(model)}
                    </span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-3 text-[11px] text-muted-foreground">
                    <span className="shrink-0">{model.providerName}</span>
                    {model.isPinned ? <span className="shrink-0">Pinned</span> : null}
                    {model.status !== 'active' ? <span className="shrink-0 capitalize">{model.status}</span> : null}
                    <ScoreBar
                      value={model.categoryScore}
                      className="flex-1"
                      testId={`catalog-score-bar-${model.id}`}
                    />
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}

export function useFilteredModelCount(models: CatalogModel[]) {
  return useMemo(() => models.length, [models])
}
